import { useEffect, useState } from "react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, AreaChart, Area } from "recharts";
import { useI18n } from "@/lib/i18n";
import { api } from "@/lib/api";

const BLUE = "#2563EB";
const RANGES = [[7, "o.d7"], [30, "o.d30"], [90, "o.d90"], [365, "o.d365"], [0, "o.dall"]];

function Kpi({ label, value, hint, id }) {
  return (
    <div className="rounded-2xl bg-white border border-slate-200 p-4" data-testid={id}>
      <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">{label}</p>
      <p className="font-mono text-2xl font-semibold mt-1">{value ?? "—"}</p>
      {hint && <p className="text-[11px] text-slate-400 mt-0.5">{hint}</p>}
    </div>
  );
}

function ChartCard({ title, children, id, className = "" }) {
  return (
    <div className={`rounded-2xl bg-white border border-slate-200 p-5 ${className}`} data-testid={id}>
      <h3 className="font-semibold mb-4">{title}</h3>
      {children}
    </div>
  );
}

function Bars({ data, x, height = 260, vertical }) {
  const { t } = useI18n();
  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart data={data} layout={vertical ? "vertical" : "horizontal"} margin={{ left: vertical ? 10 : -20, right: 10 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
        {vertical ? <><XAxis type="number" allowDecimals={false} fontSize={11} /><YAxis type="category" dataKey={x} width={150} fontSize={11} /></>
          : <><XAxis dataKey={x} fontSize={11} interval={0} /><YAxis allowDecimals={false} fontSize={11} /></>}
        <Tooltip />
        <Bar dataKey="count" name={t("o.count")} fill={BLUE} radius={vertical ? [0, 6, 6, 0] : [6, 6, 0, 0]} />
      </BarChart>
    </ResponsiveContainer>
  );
}

function Tables({ d, t }) {
  return (
    <div className="grid lg:grid-cols-2 gap-4">
      <ChartCard title={t("o.c_pros")} id="analytics-pros">
        <table className="w-full text-sm">
          <thead className="text-xs text-slate-500 text-start"><tr>{["o.h_pro", "o.p_assigned", "o.p_done", "o.p_declined", "o.p_accept", "o.p_rev"].map((k) => t(k)).map((h) => <th key={h} className="text-start py-1">{h}</th>)}</tr></thead>
          <tbody>{d.pros.map((p) => (
            <tr key={p.name} className="border-t"><td className="py-1.5">{p.name}</td><td>{p.assigned}</td><td>{p.completed}</td><td>{p.declined}</td><td>{p.acceptance_rate ?? "—"}</td><td className="font-mono text-xs">{p.revenue_nio} / {p.revenue_usd}</td></tr>
          ))}</tbody>
        </table>
        {d.pros.length === 0 && <p className="text-sm text-slate-400">{t("o.no_assign")}</p>}
      </ChartCard>
      <ChartCard title={t("o.c_gaps")} id="analytics-gaps">
        <p className="text-xs text-slate-500 mb-3">{t("o.gaps_d")}</p>
        {d.gaps.length === 0 ? <p className="text-sm text-slate-400">{t("o.no_gaps")}</p> : (
          <ul className="space-y-1.5 text-sm">{d.gaps.map((g) => <li key={g.service + g.city} className="flex justify-between border-b pb-1"><span>{t(`svc.${g.service}`)} · {g.city}</span><span className="font-mono">{g.count}</span></li>)}</ul>
        )}
      </ChartCard>
    </div>
  );
}

export default function OwnerAnalytics() {
  const { t } = useI18n();
  const [days, setDays] = useState(90);
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/owner/analytics", { params: { days } }).then((r) => setD(r.data)); }, [days]);
  if (!d) return null;
  const hours = d.by_hour.map((h) => ({ ...h, label: `${h.hour}h` }));
  const peak = [...d.by_hour].sort((a, b) => b.count - a.count)[0];
  const services = d.by_service.map((s) => ({ ...s, name: t(`svc.${s.key}`) }));
  const DAYS = t("o.days").split(",");
  const pct = (n) => t("o.k_pct", { n: Math.round((n * 100) / d.total) });
  const rev = Object.entries(d.approved_revenue).map(([c, v]) => `${v} ${c}`).join(" · ") || "—";
  return (
    <div className="space-y-4" data-testid="owner-analytics">
      <div className="flex flex-wrap gap-2">
        {RANGES.map(([v, l]) => (
          <button key={v} onClick={() => setDays(v)} data-testid={`analytics-range-${v}`}
            className={`px-4 py-1.5 rounded-full text-sm border ${days === v ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-300"}`}>{t(l)}</button>
        ))}
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-8 gap-3">
        <Kpi id="kpi-total" label={t("o.k_requests")} value={d.total} />
        <Kpi id="kpi-urgent" label={t("o.k_urgent")} value={d.total ? `${Math.round((d.urgent * 100) / d.total)}%` : "—"} hint={t("o.k_n_requests", { n: d.urgent })} />
        <Kpi id="kpi-completed" label={t("o.k_completed")} value={d.completed} hint={d.total ? pct(d.completed) : ""} />
        <Kpi id="kpi-canceled" label={t("o.k_canceled")} value={d.canceled} />
        <Kpi id="kpi-assign" label={t("o.k_assign")} value={d.avg_hours_to_assign != null ? `${d.avg_hours_to_assign}h` : null} />
        <Kpi id="kpi-complete" label={t("o.k_complete")} value={d.avg_hours_to_complete != null ? `${d.avg_hours_to_complete}h` : null} />
        <Kpi id="kpi-approval" label={t("o.k_approval")} value={d.estimate_approval_rate != null ? `${d.estimate_approval_rate}%` : null} />
        <Kpi id="kpi-revenue" label={t("o.k_revenue")} value={rev} hint={t("o.k_multi", { n: d.multi_job_requests })} />
      </div>
      <div className="grid lg:grid-cols-2 gap-4">
        <ChartCard title={t("o.c_services")} id="analytics-services"><Bars data={services} x="name" vertical height={Math.max(220, services.length * 28)} /></ChartCard>
        <ChartCard title={`${t("o.c_hours")}${peak?.count ? ` — ${t("o.c_peak", { h: peak.hour })}` : ""}`} id="analytics-hours"><Bars data={hours} x="label" /></ChartCard>
        <ChartCard title={t("o.c_weekday")} id="analytics-weekdays"><Bars data={d.by_weekday.map((w) => ({ ...w, label: DAYS[w.day] }))} x="label" height={220} /></ChartCard>
        <ChartCard title={t("o.c_cities")} id="analytics-cities"><Bars data={d.by_city} x="name" vertical height={Math.max(180, d.by_city.length * 32)} /></ChartCard>
        <ChartCard title={t("o.c_daily")} id="analytics-daily" className="lg:col-span-2">
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={d.daily} margin={{ left: -20, right: 10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" /><XAxis dataKey="date" fontSize={11} /><YAxis allowDecimals={false} fontSize={11} /><Tooltip />
              <Area dataKey="count" name={t("o.count")} stroke={BLUE} fill={BLUE} fillOpacity={0.15} />
            </AreaChart>
          </ResponsiveContainer>
        </ChartCard>
        <ChartCard title={t("o.c_status")} id="analytics-status" className="lg:col-span-2"><Bars data={d.by_status.map((s) => ({ ...s, label: t(`st.${s.key}`) }))} x="label" height={240} /></ChartCard>
      </div>
      <Tables d={d} t={t} />
    </div>
  );
}
